import * as Sentry from '@sentry/react';

/**
 * Returns false for auth failures that should not be reported to Sentry.
 */
export function shouldCaptureApiFailure(status?: number | null): boolean {
  return status !== 401;
}

export function isUnauthorizedError(error: unknown): boolean {
  if (!error || typeof error !== 'object') {
    return false;
  }
  const candidate = error as { status?: unknown; statusCode?: unknown };
  return candidate.status === 401 || candidate.statusCode === 401;
}

/**
 * Report an API/fetch failure to Sentry unless it is a 401.
 */
export function captureApiFailure(
  message: string,
  options: {
    status?: number | null;
    error?: unknown;
    extra?: Record<string, unknown>;
  } = {}
): void {
  if (!shouldCaptureApiFailure(options.status)) {
    return;
  }
  if (isUnauthorizedError(options.error)) {
    return;
  }
  const requestId =
    typeof options.extra?.requestId === 'string' ? options.extra.requestId : undefined;
  const tags = requestId ? { request_id: requestId } : undefined;
  const exception = options.error instanceof Error ? options.error : new Error(message);
  if (!(options.error instanceof Error)) {
    Sentry.captureException(exception, {
      tags,
      extra: {
        ...options.extra,
        status: options.status,
        originalError: options.error,
      },
    });
    return;
  }
  Sentry.captureException(exception, {
    tags,
    extra: {
      message,
      ...options.extra,
      status: options.status,
    },
  });
}
